<template>
  <div class="contract-page">
    <!-- 顶部统计卡片 -->
    <div class="stat-row">
      <div v-for="(item, index) in statCards" :key="index" class="stat-card">
        <div class="stat-left">
          <div class="stat-value" :style="{ color: item.color }">{{ item.value }}</div>
          <div class="stat-label">{{ item.label }}</div>
        </div>
        <div class="stat-icon" :style="{ background: item.bgColor, color: item.color }">{{ item.icon }}</div>
      </div>
    </div>

    <!-- 第一行：合同生命周期 + 合同类型分布 -->
    <div class="chart-row">
      <div class="chart-box chart-flow">
        <div class="chart-header">
          <span class="chart-title">◈ 合同全生命周期</span>
        </div>
        <div class="flow-steps">
          <div v-for="(step, idx) in flowSteps" :key="idx" class="flow-step" :class="{ active: step.active, done: step.done }">
            <div class="step-circle">
              <span v-if="step.done">✓</span>
              <span v-else>{{ idx + 1 }}</span>
            </div>
            <div class="step-name">{{ step.name }}</div>
            <div class="step-desc">{{ step.desc }}</div>
            <div v-if="idx < flowSteps.length - 1" class="step-arrow">→</div>
          </div>
        </div>
      </div>
      <div class="chart-box chart-small">
        <div class="chart-header">
          <span class="chart-title">◉ 合同类型分布</span>
        </div>
        <div ref="chartType" class="chart-body"></div>
      </div>
    </div>

    <!-- 第二行：月度合同金额趋势 + 履约进度分布 -->
    <div class="chart-row">
      <div class="chart-box chart-medium">
        <div class="chart-header">
          <span class="chart-title">▣ 月度合同金额趋势</span>
          <span class="chart-sub">单位：万元</span>
        </div>
        <div ref="chartAmountTrend" class="chart-body"></div>
      </div>
      <div class="chart-box chart-medium">
        <div class="chart-header">
          <span class="chart-title">◆ 合同履约进度分布</span>
        </div>
        <div ref="chartProgress" class="chart-body"></div>
      </div>
    </div>

    <!-- 底部：合同管理列表 -->
    <div class="chart-box table-box">
      <div class="chart-header">
        <span class="chart-title">◊ 合同管理列表</span>
        <div class="table-actions">
          <button class="action-btn" @click="handleNewContract">新建合同</button>
          <button class="action-btn" @click="handleExport">导出台账</button>
        </div>
      </div>
      <div class="table-wrap">
        <table class="data-table">
          <thead>
            <tr>
              <th>合同编号</th>
              <th>合同名称</th>
              <th>合同类型</th>
              <th>供应商</th>
              <th>合同金额</th>
              <th>开始日期</th>
              <th>结束日期</th>
              <th>履约进度</th>
              <th>合同状态</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, idx) in contractList" :key="idx">
              <td class="code-cell">{{ item.code }}</td>
              <td class="name-cell">
                <div class="contract-name">{{ item.name }}</div>
              </td>
              <td>
                <span class="type-tag" :class="item.type">{{ typeText(item.type) }}</span>
              </td>
              <td class="supplier-cell">{{ item.supplier }}</td>
              <td class="amount-cell">{{ item.amount }}</td>
              <td>{{ item.startDate }}</td>
              <td :class="{ urgent: item.isExpiring }">{{ item.endDate }}</td>
              <td>
                <div class="progress-wrap">
                  <div class="progress-bar">
                    <div class="progress-fill" :style="{ width: item.progress + '%', background: progressColor(item.progress) }"></div>
                  </div>
                  <span class="progress-text">{{ item.progress }}%</span>
                </div>
              </td>
              <td>
                <span class="status-badge" :class="item.status">{{ statusText(item.status) }}</span>
              </td>
              <td>
                <button class="op-btn primary" @click="handleView(item)">查看</button>
                <button class="op-btn" @click="handleRenew(item)">续签</button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="table-footer">
        <span class="footer-info">共 {{ contractList.length }} 条记录</span>
        <div class="pagination">
          <button class="page-btn" :disabled="currentPage === 1" @click="currentPage--">←</button>
          <span class="page-num" v-for="p in pageList" :key="p" :class="{ active: p === currentPage }" @click="currentPage = p">{{ p }}</span>
          <button class="page-btn" :disabled="currentPage === totalPage" @click="currentPage++">→</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SrmContract',
  data() {
    return {
      statCards: [
        { label: '合同总数', value: '386', icon: '▣', color: '#00d4ff', bgColor: 'rgba(0, 212, 255, 0.1)' },
        { label: '履约中', value: '218', icon: '◈', color: '#00e5a0', bgColor: 'rgba(0, 229, 160, 0.1)' },
        { label: '待审批', value: '24', icon: '◉', color: '#f0a030', bgColor: 'rgba(240, 160, 48, 0.1)' },
        { label: '本月到期', value: '18', icon: '◊', color: '#ff6b6b', bgColor: 'rgba(255, 107, 107, 0.1)' },
        { label: '已归档', value: '142', icon: '✓', color: '#8c9bb3', bgColor: 'rgba(140, 155, 179, 0.1)' },
        { label: '合同总金额', value: '¥8.56亿', icon: '◆', color: '#a855f7', bgColor: 'rgba(168, 85, 247, 0.1)' }
      ],
      flowSteps: [
        { name: '合同起草', desc: '拟定合同条款', done: true, active: false },
        { name: '内部审批', desc: '法务财务审核', done: true, active: false },
        { name: '供应商确认', desc: '双方协商确认', done: true, active: false },
        { name: '正式签署', desc: '盖章生效', done: false, active: true },
        { name: '履约执行', desc: '按约执行交付', done: false, active: false },
        { name: '到期归档', desc: '结算归档', done: false, active: false }
      ],
      contractList: [
        { code: 'CT-2026-0156', name: '2026年度电子元器件采购框架协议', type: 'frame', supplier: '深圳华强电子', amount: '¥2,500万', startDate: '2026-01-01', endDate: '2026-12-31', progress: 52, status: 'performing', isExpiring: false },
        { code: 'CT-2026-0155', name: '精密机械零部件加工服务合同', type: 'purchase', supplier: '苏州精密制造', amount: '¥860万', startDate: '2026-03-15', endDate: '2026-09-15', progress: 78, status: 'performing', isExpiring: false },
        { code: 'CT-2026-0154', name: '化工原材料年度采购合同', type: 'purchase', supplier: '青岛海润化工', amount: '¥1,200万', startDate: '2026-02-01', endDate: '2026-06-30', progress: 92, status: 'performing', isExpiring: true },
        { code: 'CT-2026-0153', name: '智能仓储设备租赁合同', type: 'lease', supplier: '北京自动化设备', amount: '¥350万', startDate: '2026-04-01', endDate: '2027-03-31', progress: 25, status: 'performing', isExpiring: false },
        { code: 'CT-2026-0152', name: '物流运输服务框架协议', type: 'frame', supplier: '顺丰供应链', amount: '¥420万', startDate: '2026-01-01', endDate: '2026-12-31', progress: 48, status: 'performing', isExpiring: false },
        { code: 'CT-2026-0151', name: '技术保密与知识产权协议', type: 'nda', supplier: '杭州半导体', amount: '—', startDate: '2026-05-01', endDate: '2028-04-30', progress: 10, status: 'performing', isExpiring: false },
        { code: 'CT-2026-0150', name: '包装材料采购合同', type: 'purchase', supplier: '东莞包装材料', amount: '¥180万', startDate: '2026-06-01', endDate: '2026-08-31', progress: 5, status: 'pending', isExpiring: false },
        { code: 'CT-2026-0149', name: '设备维护服务合同', type: 'service', supplier: '成都西部智能', amount: '¥120万', startDate: '2026-06-15', endDate: '2027-06-14', progress: 0, status: 'approving', isExpiring: false }
      ],
      currentPage: 1,
      totalPage: 3,
      charts: {}
    }
  },
  computed: {
    pageList() {
      return [1, 2, 3]
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.initTypeChart()
      this.initAmountTrendChart()
      this.initProgressChart()
    })
    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
    Object.values(this.charts).forEach(chart => chart && chart.dispose())
  },
  methods: {
    handleResize() {
      Object.values(this.charts).forEach(chart => chart && chart.resize())
    },
    typeText(type) {
      const map = { purchase: '采购合同', service: '服务合同', frame: '框架协议', lease: '租赁合同', nda: '保密协议' }
      return map[type] || type
    },
    statusText(status) {
      const map = { drafting: '起草中', approving: '审批中', pending: '待签署', performing: '履约中', completed: '已完成', archived: '已归档' }
      return map[status] || status
    },
    progressColor(progress) {
      if (progress >= 80) return '#00e5a0'
      if (progress >= 50) return '#00d4ff'
      if (progress >= 20) return '#f0a030'
      return '#ff6b6b'
    },
    handleNewContract() {
      alert('打开新建合同弹窗')
    },
    handleExport() {
      alert('正在导出合同台账...')
    },
    handleView(item) {
      alert(`查看合同详情：${item.name}\n\n合同编号：${item.code}\n供应商：${item.supplier}\n合同金额：${item.amount}\n履约进度：${item.progress}%`)
    },
    handleRenew(item) {
      alert(`发起续签流程：${item.name}`)
    },

    // 合同类型分布（饼图）
    initTypeChart() {
      const chart = this.$echarts.init(this.$refs.chartType)
      this.charts.type = chart
      chart.setOption({
        backgroundColor: 'transparent',
        tooltip: {
          trigger: 'item',
          backgroundColor: 'rgba(1, 16, 57, 0.9)',
          borderColor: 'rgba(0, 212, 255, 0.3)',
          textStyle: { color: '#fff' },
          formatter: '{b}: {c}份 ({d}%)'
        },
        legend: {
          orient: 'vertical',
          right: 10,
          top: 'center',
          textStyle: { color: '#8c9bb3', fontSize: 11 },
          itemWidth: 10,
          itemHeight: 10
        },
        series: [{
          type: 'pie',
          radius: ['40%', '65%'],
          center: ['35%', '50%'],
          avoidLabelOverlap: false,
          label: { show: false },
          labelLine: { show: false },
          data: [
            { value: 186, name: '采购合同', itemStyle: { color: '#00d4ff' } },
            { value: 68, name: '框架协议', itemStyle: { color: '#00e5a0' } },
            { value: 52, name: '服务合同', itemStyle: { color: '#f0a030' } },
            { value: 48, name: '租赁合同', itemStyle: { color: '#ff6b9d' } },
            { value: 32, name: '保密协议', itemStyle: { color: '#a855f7' } }
          ]
        }]
      })
    },

    // 月度合同金额趋势（折线+柱状混合）
    initAmountTrendChart() {
      const chart = this.$echarts.init(this.$refs.chartAmountTrend)
      this.charts.amountTrend = chart
      chart.setOption({
        backgroundColor: 'transparent',
        tooltip: {
          trigger: 'axis',
          backgroundColor: 'rgba(1, 16, 57, 0.9)',
          borderColor: 'rgba(0, 212, 255, 0.3)',
          textStyle: { color: '#fff' }
        },
        legend: {
          data: ['新签合同', '到期结算'],
          textStyle: { color: '#8c9bb3' },
          right: 10,
          top: 0
        },
        grid: { left: 50, right: 20, top: 40, bottom: 25 },
        xAxis: {
          type: 'category',
          data: ['1月', '2月', '3月', '4月', '5月', '6月'],
          axisLine: { lineStyle: { color: 'rgba(140, 155, 179, 0.2)' } },
          axisLabel: { color: '#8c9bb3', fontSize: 11 },
          axisTick: { show: false }
        },
        yAxis: {
          type: 'value',
          axisLine: { show: false },
          axisLabel: { color: '#8c9bb3', fontSize: 11 },
          splitLine: { lineStyle: { color: 'rgba(140, 155, 179, 0.1)' } }
        },
        series: [
          {
            name: '新签合同',
            type: 'bar',
            barWidth: 16,
            itemStyle: {
              color: new this.$echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: '#00d4ff' },
                { offset: 1, color: 'rgba(0, 212, 255, 0.2)' }
              ]),
              borderRadius: [4, 4, 0, 0]
            },
            data: [4200, 3800, 5600, 4800, 5200, 6100]
          },
          {
            name: '到期结算',
            type: 'line',
            smooth: true,
            symbol: 'circle',
            symbolSize: 6,
            lineStyle: { color: '#f0a030', width: 2 },
            itemStyle: { color: '#f0a030' },
            data: [2800, 3200, 3500, 4100, 3800, 4500]
          }
        ]
      })
    },

    // 合同履约进度分布（横向条形图）
    initProgressChart() {
      const chart = this.$echarts.init(this.$refs.chartProgress)
      this.charts.progress = chart
      chart.setOption({
        backgroundColor: 'transparent',
        tooltip: {
          trigger: 'axis',
          backgroundColor: 'rgba(1, 16, 57, 0.9)',
          borderColor: 'rgba(0, 212, 255, 0.3)',
          textStyle: { color: '#fff' },
          axisPointer: { type: 'shadow' }
        },
        grid: { left: 80, right: 40, top: 10, bottom: 10 },
        xAxis: {
          type: 'value',
          max: 100,
          axisLine: { show: false },
          axisLabel: { color: '#8c9bb3', fontSize: 10, formatter: '{value}%' },
          splitLine: { lineStyle: { color: 'rgba(140, 155, 179, 0.1)' } }
        },
        yAxis: {
          type: 'category',
          data: ['0-20%', '20-50%', '50-80%', '80-100%'],
          axisLine: { show: false },
          axisTick: { show: false },
          axisLabel: { color: '#8c9bb3', fontSize: 12 }
        },
        series: [{
          type: 'bar',
          barWidth: 22,
          data: [
            { value: 28, itemStyle: { color: '#ff6b6b', borderRadius: [0, 4, 4, 0] } },
            { value: 65, itemStyle: { color: '#f0a030', borderRadius: [0, 4, 4, 0] } },
            { value: 82, itemStyle: { color: '#00d4ff', borderRadius: [0, 4, 4, 0] } },
            { value: 56, itemStyle: { color: '#00e5a0', borderRadius: [0, 4, 4, 0] } }
          ],
          label: {
            show: true,
            position: 'right',
            color: '#fff',
            fontSize: 12,
            formatter: '{c}份'
          }
        }]
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.contract-page {
  width: 100%;
  min-height: 100%;
  padding-bottom: 20px;
}

/* ==================== 统计卡片 ==================== */
.stat-row {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 16px;
  margin-bottom: 16px;
}

.stat-card {
  background: rgba(0, 212, 255, 0.04);
  border: 1px solid rgba(0, 212, 255, 0.1);
  border-radius: 10px;
  padding: 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: all 0.3s;

  &:hover {
    background: rgba(0, 212, 255, 0.07);
    border-color: rgba(0, 212, 255, 0.2);
    transform: translateY(-2px);
  }
}

.stat-left {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.stat-value {
  font-size: 24px;
  font-weight: bold;
  letter-spacing: 1px;
}

.stat-label {
  font-size: 12px;
  color: #8c9bb3;
}

.stat-icon {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  flex-shrink: 0;
}

/* ==================== 图表行 ==================== */
.chart-row {
  display: grid;
  gap: 16px;
  margin-bottom: 16px;

  &:nth-of-type(2) {
    grid-template-columns: 1.8fr 1fr;
  }

  &:nth-of-type(3) {
    grid-template-columns: 1fr 1fr;
  }
}

.chart-box {
  background: rgba(0, 212, 255, 0.03);
  border: 1px solid rgba(0, 212, 255, 0.1);
  border-radius: 10px;
  padding: 16px;
  display: flex;
  flex-direction: column;
}

.chart-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
  flex-shrink: 0;
}

.chart-title {
  font-size: 14px;
  color: #fff;
  font-weight: bold;
  letter-spacing: 1px;
}

.chart-sub {
  font-size: 11px;
  color: #8c9bb3;
}

.chart-body {
  flex: 1;
  min-height: 240px;
}

/* ==================== 生命周期流程 ==================== */
.flow-steps {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 20px 10px;
  flex: 1;
}

.flow-step {
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  flex: 1;

  .step-circle {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    border: 2px solid rgba(140, 155, 179, 0.3);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    color: #8c9bb3;
    background: rgba(1, 16, 57, 0.8);
    transition: all 0.3s;
    z-index: 2;
  }

  .step-name {
    font-size: 13px;
    color: #8c9bb3;
    margin-top: 10px;
    font-weight: bold;
    text-align: center;
  }

  .step-desc {
    font-size: 11px;
    color: rgba(140, 155, 179, 0.6);
    margin-top: 4px;
    text-align: center;
  }

  .step-arrow {
    position: absolute;
    right: -20px;
    top: 12px;
    color: rgba(140, 155, 179, 0.3);
    font-size: 16px;
    z-index: 1;
  }

  &.done {
    .step-circle {
      border-color: #00e5a0;
      color: #00e5a0;
      background: rgba(0, 229, 160, 0.1);
    }
    .step-name { color: #00e5a0; }
  }

  &.active {
    .step-circle {
      border-color: #00d4ff;
      color: #00d4ff;
      background: rgba(0, 212, 255, 0.15);
      box-shadow: 0 0 15px rgba(0, 212, 255, 0.3);
      animation: stepPulse 2s infinite;
    }
    .step-name { color: #00d4ff; }
  }
}

@keyframes stepPulse {
  0%, 100% { box-shadow: 0 0 10px rgba(0, 212, 255, 0.2); }
  50% { box-shadow: 0 0 20px rgba(0, 212, 255, 0.5); }
}

/* ==================== 表格区域 ==================== */
.table-box {
  min-height: 400px;
}

.table-actions {
  display: flex;
  gap: 10px;
}

.action-btn {
  padding: 6px 14px;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  border: 1px solid rgba(0, 212, 255, 0.4);
  background: rgba(0, 212, 255, 0.08);
  color: #00d4ff;
  transition: all 0.3s;

  &:hover {
    background: rgba(0, 212, 255, 0.15);
    box-shadow: 0 0 10px rgba(0, 212, 255, 0.2);
  }
}

.table-wrap {
  flex: 1;
  overflow-x: auto;

  &::-webkit-scrollbar {
    height: 4px;
  }
  &::-webkit-scrollbar-track {
    background: rgba(0, 212, 255, 0.05);
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(0, 212, 255, 0.2);
    border-radius: 2px;
  }
}

.data-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;

  th, td {
    padding: 12px 14px;
    text-align: left;
    border-bottom: 1px solid rgba(0, 212, 255, 0.08);
    white-space: nowrap;
  }

  th {
    background: rgba(0, 212, 255, 0.06);
    color: #8c9bb3;
    font-size: 12px;
    font-weight: normal;
    letter-spacing: 1px;
  }

  tbody tr {
    transition: all 0.2s;

    &:hover {
      background: rgba(0, 212, 255, 0.04);
    }
  }
}

.code-cell {
  color: #8c9bb3;
  font-family: 'Courier New', monospace;
  font-size: 12px;
}

.name-cell {
  .contract-name {
    color: #fff;
    font-size: 13px;
    max-width: 240px;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

.supplier-cell {
  color: #c8d4e8;
  font-size: 13px;
}

.amount-cell {
  color: #00d4ff;
  font-weight: bold;
}

.urgent {
  color: #ff6b6b;
  font-weight: bold;
}

.type-tag {
  padding: 2px 10px;
  border-radius: 4px;
  font-size: 12px;

  &.purchase {
    background: rgba(0, 212, 255, 0.1);
    color: #00d4ff;
  }

  &.service {
    background: rgba(0, 229, 160, 0.1);
    color: #00e5a0;
  }

  &.frame {
    background: rgba(240, 160, 48, 0.1);
    color: #f0a030;
  }

  &.lease {
    background: rgba(255, 107, 157, 0.1);
    color: #ff6b9d;
  }

  &.nda {
    background: rgba(168, 85, 247, 0.1);
    color: #a855f7;
  }
}

.progress-wrap {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 100px;
}

.progress-bar {
  flex: 1;
  height: 6px;
  background: rgba(140, 155, 179, 0.15);
  border-radius: 3px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  border-radius: 3px;
  transition: width 0.5s ease;
}

.progress-text {
  font-size: 11px;
  color: #8c9bb3;
  flex-shrink: 0;
  width: 36px;
}

.status-badge {
  padding: 3px 10px;
  border-radius: 4px;
  font-size: 12px;

  &.drafting {
    background: rgba(140, 155, 179, 0.1);
    color: #8c9bb3;
  }

  &.approving {
    background: rgba(240, 160, 48, 0.1);
    color: #f0a030;
  }

  &.pending {
    background: rgba(0, 212, 255, 0.1);
    color: #00d4ff;
  }

  &.performing {
    background: rgba(0, 229, 160, 0.1);
    color: #00e5a0;
  }

  &.completed {
    background: rgba(168, 85, 247, 0.1);
    color: #a855f7;
  }

  &.archived {
    background: rgba(140, 155, 179, 0.1);
    color: #8c9bb3;
  }
}

.op-btn {
  padding: 3px 10px;
  border-radius: 4px;
  font-size: 11px;
  cursor: pointer;
  border: none;
  margin-right: 6px;
  transition: all 0.2s;

  &.primary {
    background: rgba(0, 212, 255, 0.15);
    color: #00d4ff;

    &:hover {
      background: rgba(0, 212, 255, 0.3);
    }
  }

  &:not(.primary) {
    background: rgba(140, 155, 179, 0.1);
    color: #8c9bb3;

    &:hover {
      background: rgba(0, 212, 255, 0.1);
      color: #00d4ff;
    }
  }
}

/* ==================== 分页 ==================== */
.table-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 12px;
  border-top: 1px solid rgba(0, 212, 255, 0.08);
  margin-top: 8px;
}

.footer-info {
  font-size: 12px;
  color: #8c9bb3;
}

.pagination {
  display: flex;
  align-items: center;
  gap: 6px;
}

.page-btn {
  width: 28px;
  height: 28px;
  border-radius: 4px;
  border: 1px solid rgba(0, 212, 255, 0.2);
  background: transparent;
  color: #8c9bb3;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    border-color: #00d4ff;
    color: #00d4ff;
    background: rgba(0, 212, 255, 0.08);
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
}

.page-num {
  width: 28px;
  height: 28px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  color: #8c9bb3;
  cursor: pointer;
  border: 1px solid transparent;
  transition: all 0.2s;

  &:hover {
    border-color: rgba(0, 212, 255, 0.3);
    color: #00d4ff;
  }

  &.active {
    background: rgba(0, 212, 255, 0.15);
    border-color: #00d4ff;
    color: #00d4ff;
  }
}

/* ==================== 响应式 ==================== */
@media (max-width: 1400px) {
  .stat-row {
    grid-template-columns: repeat(3, 1fr);
  }

  .chart-row:nth-of-type(2),
  .chart-row:nth-of-type(3) {
    grid-template-columns: 1fr;
  }

  .flow-steps {
    flex-wrap: wrap;
    gap: 16px;

    .flow-step {
      min-width: 100px;

      .step-arrow {
        display: none;
      }
    }
  }
}

@media (max-width: 768px) {
  .stat-row {
    grid-template-columns: repeat(2, 1fr);
  }

  .chart-body {
    min-height: 200px;
  }

  .op-btn {
    padding: 2px 6px;
    font-size: 10px;
    margin-right: 3px;
  }
}
</style>